import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import PublicStorefront from "@/pages/PublicStorefront";
import PaymentReturn from "@/pages/PaymentReturn";
import { PrivacyPage, TermsPage } from "@/pages/Legal";
import { ForgotPassword, ResetPassword } from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import { DashboardLayout } from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Products from "@/pages/dashboard/Products";
import BakeDays from "@/pages/dashboard/BakeDays";
import Orders from "@/pages/dashboard/Orders";
import Recipes from "@/pages/dashboard/Recipes";
import Customers from "@/pages/dashboard/Customers";
import StorefrontSettings from "@/pages/dashboard/StorefrontSettings";
import Settings from "@/pages/dashboard/Settings";
import { Loader2 } from "lucide-react";

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cream">
      <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
    </div>
  );
}

function Protected({ children, requireBakery = true, admin = false }) {
  const { loading, user, bakery } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (requireBakery && user.role !== "admin" && !bakery) return <Navigate to="/onboarding" replace />;
  return children;
}

function LegacyStoreRedirect() {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/bakery/${slug}${search}`} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Protected requireBakery={false}><Onboarding /></Protected>} />
      <Route path="/bakery/:slug" element={<PublicStorefront />} />
      <Route path="/b/:slug" element={<LegacyStoreRedirect />} />
      <Route path="/payment/success" element={<PaymentReturn />} />
      <Route path="/payment/cancel" element={<PaymentReturn />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
        <Route index element={<Overview />} />
        <Route path="bake-days" element={<BakeDays />} />
        <Route path="calendar" element={<Navigate to="/dashboard/bake-days" replace />} />
        <Route path="promotions" element={<Navigate to="/dashboard/bake-days" replace />} />
        <Route path="orders" element={<Orders />} />
        <Route path="products" element={<Products />} />
        <Route path="recipes" element={<Recipes />} />
        <Route path="customers" element={<Customers />} />
        <Route path="storefront" element={<StorefrontSettings />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="bottom-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
